/**
 * プール向けの安い水面。頂点変位はせず、法線スクロール・Fresnel・深さ吸収だけ。
 * 不透明パスの色と深度を画面空間で読み、水面ピクセルへ合成する。
 */

import * as THREE from 'three';
import {
  SKY_GROUND,
  SKY_HORIZON,
  SKY_ZENITH,
  SUN_COLOR,
  SUN_DIRECTION,
} from './environment';

const VERTEX = /* glsl */ `
varying vec3 vWorldPosition;
varying vec3 vViewPosition;
varying vec3 vWorldNormal;

void main() {
  vec4 world = vec4(position, 1.0);
  vec3 objectNormal = normal;
#ifdef USE_INSTANCING
  world = instanceMatrix * world;
  objectNormal = mat3(instanceMatrix) * objectNormal;
#endif
  world = modelMatrix * world;
  vWorldPosition = world.xyz;
  vWorldNormal = normalize(mat3(modelMatrix) * objectNormal);
  vec4 mv = viewMatrix * world;
  vViewPosition = mv.xyz;
  gl_Position = projectionMatrix * mv;
}
`;

const FRAGMENT = /* glsl */ `
#include <packing>

uniform sampler2D tScene;
uniform sampler2D tDepth;
uniform float cameraNear;
uniform float cameraFar;
uniform vec2 resolution;
uniform float time;
uniform vec3 sunDirection;
uniform float sunEnabled;
uniform vec3 sunColor;
uniform vec3 skyZenith;
uniform vec3 skyHorizon;
uniform vec3 skyGround;

varying vec3 vWorldPosition;
varying vec3 vViewPosition;
varying vec3 vWorldNormal;

vec3 skyColor(vec3 dir) {
  vec3 d = normalize(dir);
  float h = d.y;
  vec3 sky = mix(skyHorizon, skyZenith, clamp(h * 0.85 + 0.15, 0.0, 1.0));
  vec3 below = mix(skyHorizon, skyGround, clamp(-h * 1.4, 0.0, 1.0));
  vec3 color = mix(below, sky, step(0.0, h));
  if (sunEnabled < 0.5) return color;
  float sunDot = clamp(dot(d, -normalize(sunDirection)), 0.0, 1.0);
  float disc = smoothstep(0.9994, 0.99985, sunDot);
  float halo = pow(sunDot, 48.0) * 0.45;
  return color + sunColor * (disc * 2.2 + halo) / max(length(sunColor), 1e-4);
}

vec2 waveGrad(vec2 p, vec2 dir, float freq, float speed, float amp) {
  float c = cos(dot(p, dir) * freq + time * speed) * amp * freq;
  return dir * c;
}

vec3 waterNormal(vec3 geomNormal, vec3 worldPos) {
  vec3 n = normalize(geomNormal);
  vec3 t = normalize(cross(abs(n.y) > 0.9 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0), n));
  vec3 b = cross(n, t);
  vec2 p = vec2(dot(worldPos, t), dot(worldPos, b));
  vec2 g = waveGrad(p, normalize(vec2(1.0, 0.18)), 2.6, 1.35, 0.011)
    + waveGrad(p, normalize(vec2(-0.22, 1.0)), 3.3, -1.15, 0.009)
    + waveGrad(p, normalize(vec2(0.72, 0.69)), 5.4, 1.85, 0.0045)
    + waveGrad(p, normalize(vec2(-0.81, 0.58)), 9.2, 2.45, 0.0022);
  return normalize(t * -g.x + b * -g.y + n);
}

void main() {
  vec3 geomN = normalize(vWorldNormal);
  vec3 viewVec = cameraPosition - vWorldPosition;
  if (dot(geomN, viewVec) < 0.0) geomN = -geomN;

  vec3 N = waterNormal(geomN, vWorldPosition);
  vec3 V = normalize(viewVec);
  vec2 uv = gl_FragCoord.xy / resolution;
  vec2 refractUV = clamp(uv + N.xz * 0.035, vec2(0.002), vec2(0.998));

  float waterDepth = texture2D(tDepth, uv).r;
  float sceneDepth = texture2D(tDepth, refractUV).r;
  float waterViewZ = vViewPosition.z;
  float sceneViewZ = perspectiveDepthToViewZ(sceneDepth, cameraNear, cameraFar);
  if (sceneViewZ > waterViewZ + 0.02) {
    refractUV = uv;
    sceneViewZ = perspectiveDepthToViewZ(waterDepth, cameraNear, cameraFar);
  }

  float thickness = clamp(waterViewZ - sceneViewZ, 0.0, 8.0);
  float absorb = 1.0 - exp(-thickness * 0.42);
  vec3 scene = texture2D(tScene, refractUV).rgb;
  vec3 deep = vec3(0.03, 0.20, 0.30);
  vec3 transmitted = mix(scene * vec3(0.72, 0.90, 0.94), deep, absorb);

  float ndotv = max(dot(N, V), 0.0);
  float fresnel = 0.02 + 0.98 * pow(1.0 - ndotv, 5.0);
  vec3 reflected = skyColor(reflect(-V, N));
  vec3 color = mix(transmitted, reflected, fresnel);

  if (sunEnabled > 0.5) {
    vec3 L = -normalize(sunDirection);
    vec3 H = normalize(L + V);
    float spec = pow(max(dot(N, H), 0.0), 160.0);
    color += sunColor * spec * 0.55;
  }

  gl_FragColor = vec4(color, 1.0);
}
`;

export function createWaterMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      tScene: { value: null },
      tDepth: { value: null },
      cameraNear: { value: 0.05 },
      cameraFar: { value: 1200 },
      resolution: { value: new THREE.Vector2(1, 1) },
      time: { value: 0 },
      sunDirection: { value: SUN_DIRECTION.clone() },
      sunEnabled: { value: 1 },
      sunColor: { value: SUN_COLOR.clone() },
      skyZenith: { value: SKY_ZENITH.clone() },
      skyHorizon: { value: SKY_HORIZON.clone() },
      skyGround: { value: SKY_GROUND.clone() },
    },
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.NoBlending,
    side: THREE.FrontSide,
    toneMapped: false,
    fog: false,
  });
}

export function updateWaterMaterial(
  material: THREE.ShaderMaterial,
  args: {
    sceneColor: THREE.Texture;
    sceneDepth: THREE.Texture;
    camera: THREE.PerspectiveCamera;
    width: number;
    height: number;
    time: number;
    sunEnabled: boolean;
  }
): void {
  const u = material.uniforms;
  u.tScene.value = args.sceneColor;
  u.tDepth.value = args.sceneDepth;
  u.cameraNear.value = args.camera.near;
  u.cameraFar.value = args.camera.far;
  u.resolution.value.set(args.width, args.height);
  u.time.value = args.time;
  u.sunEnabled.value = args.sunEnabled ? 1 : 0;
}
